import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';
import { usePriceVisibility } from './hooks/usePriceVisibility';
import { fetchTenantCatalog, catalogTablesForRole } from './utils/catalog/catalogReadTables';
import { readSessionAal } from './utils/auth/readSessionAal';
import useAssuranceSettled from './hooks/auth/useAssuranceSettled';
import useDashboardRefreshExtra from './hooks/ui/useDashboardRefreshExtra';
import { runAfterAuthHandoff } from './ui/splash/authHandoffHold';

// KAN-589: roles whose pull refresh also reloads the catalog. Admin is absent on
// purpose — its dashboard bundle already refreshes it (refreshCatalogData).
const CATALOG_PULL_ROLES = ['billing', 'supervisor', 'worker'];

/**
 * Tenant-wide catalog of services + chemicals. Loaded once when the tenant
 * resolves; consumers (LogJob, LogJobDrawer, etc.) read from here instead of
 * each refetching the same low-frequency tables on every modal/page open.
 *
 * Wave 1K: introduced as the single source of truth for services/chemicals
 * read paths. Admin's catalog management UI (useCatalogHandlers) still owns
 * its own state for editing flows; it should call `refresh()` after a
 * mutation so this context stays in sync. Realtime subscriptions for these
 * tables are deliberately not wired up — services/chemicals change ~weekly
 * (admin edits) so on-edit refresh is sufficient.
 *
 * Tenant invalidation mirrors CompanySettingsContext: a generation counter
 * cancels in-flight fetches when tenantId changes so we never apply the
 * previous tenant's catalog after a switch.
 *
 * RD-AUD-013: operations reads `operations_services` / `operations_chemicals`
 * (no list prices). Role comes from AuthContext so we re-fetch when it
 * resolves — a store read at first paint would hit base tables, get 0 rows
 * after the SELECT revoke, and stick empty.
 *
 * RD-ROLE-005: worker/supervisor read the dollar-free `field_services` /
 * `field_chemicals` views instead when the tenant setting hides prices from
 * that role (usePriceVisibility() → canSeePrices === false).
 */

const catalogTablesKey = (t) => `${t.services}|${t.chemicals}`;

const CatalogContext = createContext({
  services: [],
  chemicals: [],
  loading: true,
  loadFailed: false,
  refresh: async () => ({ services: [], chemicals: [] }),
  peekLoaded: () => null,
});

export function CatalogProvider({ children }) {
  const { tenantId, tenantLoading, sessionUser, role } = useAuth();
  const { canSeePrices } = usePriceVisibility();
  const [services, setServices] = useState([]);
  const [chemicals, setChemicals] = useState([]);
  const [loading, setLoading] = useState(true);
  // KAN-590: true only while NOTHING is loaded and the read failed (pickers show a retry
  // instead of silently empty); a failed refresh over loaded data keeps the data.
  const [loadFailed, setLoadFailed] = useState(false);
  const hasCatalogRef = useRef(false);
  const fetchGenRef = useRef(0);
  // RD-PERF-004: the last SUCCESSFULLY committed load, keyed by tenant + the table
  // pair it read (which is what role/price visibility decide). Lets the admin
  // dashboard's first load reuse it instead of re-reading the same two tables.
  const loadedRef = useRef(null);

  const refresh = useCallback(async (activeTenantId = tenantId, { silent = false } = {}) => {
    if (!activeTenantId) {
      setServices([]);
      setChemicals([]);
      setLoading(false);
      return { services: [], chemicals: [] };
    }
    const gen = ++fetchGenRef.current;
    loadedRef.current = null; // a read is under way — its freshness is unknown until it commits
    // silent (pull extra): never flip `loading` — it disables the LogJob add buttons mid-tap.
    if (!silent) setLoading(true);
    try {
      // The identity + aal the read below runs under (pre-2FA reads are empty by RLS).
      const readAs = await readSessionAal(supabase);
      const next = await fetchTenantCatalog(supabase, activeTenantId, role, canSeePrices);
      if (gen !== fetchGenRef.current) {
        return next;
      }
      setServices(next.services);
      setChemicals(next.chemicals);
      loadedRef.current = {
        tenantId: activeTenantId,
        userId: readAs?.userId ?? null,
        aal: readAs?.aal ?? null,
        tablesKey: catalogTablesKey(catalogTablesForRole(role, canSeePrices)),
        services: next.services,
        chemicals: next.chemicals,
        at: Date.now(),
      };
      hasCatalogRef.current = true;
      setLoadFailed(false);
      setLoading(false);
      return next;
    } catch (err) {
      console.error('Catalog refresh failed:', err);
      if (gen === fetchGenRef.current) {
        if (!hasCatalogRef.current) setLoadFailed(true);
        setLoading(false);
      }
      return { services: [], chemicals: [] };
    }
  }, [tenantId, role, canSeePrices]);

  // Depend on the user *id* rather than the sessionUser reference. Supabase
  // emits fresh user objects on TOKEN_REFRESHED / focus-driven re-syncs
  // even when the signed-in user has not changed; using the object ref
  // re-runs this effect on every refresh, briefly flipping `loading: true`.
  // That brief flip disabled the LogJob "+ Add Service / Chemical" buttons
  // mid-tap and silently dropped the click — manifested as "sometimes the
  // picker won't open, especially on a second visit" (focus events from
  // navigation triggered the token re-sync). Same pattern
  // CompanySettingsContext already uses for the same reason.
  const sessionUserId = sessionUser?.id ?? null;
  // RD-SEC-051: a 2FA-enrolled user still at aal1 reads ZERO rows by RLS — wait for
  // the final assurance, and reload when the level changes (aal1 → aal2).
  const { settled: assuranceSettled, aal: sessionAal } = useAssuranceSettled(sessionUserId);
  useEffect(() => {
    if (tenantLoading) {
      loadedRef.current = null;
      setLoading(true);
      return undefined;
    }
    if (!sessionUserId) {
      fetchGenRef.current += 1;
      loadedRef.current = null;
      hasCatalogRef.current = false;
      setLoadFailed(false);
      setServices([]);
      setChemicals([]);
      setLoading(false);
      return undefined;
    }
    if (!assuranceSettled) {
      loadedRef.current = null;
      setLoading(true);
      return undefined;
    }
    // Waits out a sign-in handoff (authHandoffHold.js); `loading` is already true.
    return runAfterAuthHandoff(() => {
      void refresh(tenantId).catch((err) => {
        console.error('Catalog refresh failed:', err);
        setLoading(false);
      });
    });
  }, [refresh, tenantId, tenantLoading, sessionUserId, assuranceSettled, sessionAal]);

  // Best-effort: a failed catalog read never fails the pull (refresh() swallows).
  useDashboardRefreshExtra(() => refresh(tenantId, { silent: true }), Boolean(sessionUserId && tenantId) && CATALOG_PULL_ROLES.includes(role));

  /** Last committed load, or null. Callers must validate tenant + tablesKey + age themselves. */
  const peekLoaded = useCallback(() => loadedRef.current, []);

  const value = useMemo(() => ({
    services,
    chemicals,
    loading,
    loadFailed,
    refresh,
    peekLoaded,
  }), [services, chemicals, loading, loadFailed, refresh, peekLoaded]);

  return (
    <CatalogContext.Provider value={value}>
      {children}
    </CatalogContext.Provider>
  );
}

export const useCatalog = () => useContext(CatalogContext);
